import React, { useState } from 'react';
import { FaYoutube } from 'react-icons/fa';
import { SessionData } from './types';
import CustomMusicModal from './CustomMusicModal';
import ProjectSelector from './ProjectSelector';

interface TeamModeContentProps {
  sessionData: SessionData;
  setSessionData: React.Dispatch<React.SetStateAction<SessionData>>;
  teamTab: 'create' | 'join';
  setTeamTab: React.Dispatch<React.SetStateAction<'create' | 'join'>>;
  roomCode: string;
  setRoomCode: React.Dispatch<React.SetStateAction<string>>;
  onCreateRoom: () => void;
  onJoinRoom: () => void;
  onManageProjects?: () => void;
  onCreateProjects?: () => void;
}

const TeamModeContent: React.FC<TeamModeContentProps> = ({
  sessionData,
  setSessionData,
  teamTab,
  setTeamTab,
  roomCode,
  setRoomCode,
  onCreateRoom,
  onJoinRoom,
  onManageProjects,
  onCreateProjects,
}) => {
  const [showCustomMusicModal, setShowCustomMusicModal] = useState(false);

  const handleCustomMusicSave = (youtubeUrl: string) => {
    setSessionData({ ...sessionData, music: 'custom', customMusicURL: youtubeUrl });
  };

  const handleRemoveCustomMusic = () => {
    setSessionData({ ...sessionData, music: 'silence', customMusicURL: undefined });
  };

  return (
    <div className="bg-slate-800/20 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-orange-500/20 shadow-2xl">
      <div className="text-center mb-6 sm:mb-8">
        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Team Focus Room</h3>
        <p className="text-sm sm:text-base text-gray-300">Focus together, achieve more</p>
      </div>

      {/* Team Mode Tabs */}
      <div className="flex justify-center mb-6 sm:mb-8">
        <div className="bg-slate-700/30 rounded-xl sm:rounded-2xl p-1.5 sm:p-2 border border-orange-500/20">
          <div className="flex">
            <button
              onClick={() => setTeamTab('create')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${teamTab === 'create'
                  ? 'bg-orange-500/20 text-orange-300 border border-orange-400/30'
                  : 'text-gray-400 hover:text-white'
                }`}
            >
              Create Room
            </button>
            <button
              onClick={() => setTeamTab('join')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${teamTab === 'join'
                  ? 'bg-orange-500/20 text-orange-300 border border-orange-400/30'
                  : 'text-gray-400 hover:text-white'
                }`}
            >
              Join Room
            </button>
          </div>
        </div>
      </div>

      {/* Create Room Section */}
      {teamTab === 'create' && (
        <div className="space-y-4 sm:space-y-6">
          <div>
            <input
              type="text"
              value={sessionData.roomName}
              onChange={(e) => setSessionData({ ...sessionData, roomName: e.target.value })}
              placeholder="Enter your room name…"
              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-slate-700/50 border border-orange-500/30 rounded-xl sm:rounded-2xl text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all text-sm sm:text-base"
            />
          </div>

          {/* Project Selector */}
          <div className="bg-slate-700/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-slate-600/30">
            <ProjectSelector
              selectedProjectId={sessionData.projectId}
              onProjectChange={(projectId) => setSessionData({ ...sessionData, projectId })}
              showManageButton={true}
              onManageClick={onManageProjects}
              onCreateClick={onCreateProjects}
              required={true}
            />
          </div>

          <div>
            <h4 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Session Duration</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {[25, 45, 60].map((duration) => (
                <button
                  key={duration}
                  onClick={() => setSessionData({ ...sessionData, duration, customDuration: '' })}
                  className={`px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${sessionData.duration === duration
                      ? 'bg-orange-500/20 text-orange-300 border border-orange-400/30 shadow-lg shadow-orange-500/10'
                      : 'bg-slate-700/30 text-gray-300 border border-slate-600/30 hover:bg-slate-600/30'
                    }`}
                >
                  {duration}m
                </button>
              ))}
              <div className="relative">
                <input
                  type="text"
                  value={sessionData.customDuration}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers
                    if (value === '' || /^\d+$/.test(value)) {
                      setSessionData({ ...sessionData, customDuration: value, duration: parseInt(value) || 25 });
                    }
                  }}
                  placeholder="Custom"
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-slate-700/30 border border-slate-600/30 rounded-lg sm:rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-slate-700/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-slate-600/30 gap-3">
            <div className="flex-1 min-w-0">
              <h4 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-0">Break Reminders</h4>
              <p className="text-xs sm:text-sm text-gray-400">Optional - host decides for the team</p>
            </div>
            <button
              onClick={() => setSessionData({ ...sessionData, breakReminders: !sessionData.breakReminders })}
              className={`relative w-12 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${sessionData.breakReminders ? 'bg-orange-500' : 'bg-slate-600'
                }`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${sessionData.breakReminders ? 'translate-x-7' : 'translate-x-1'
                }`} />
            </button>
          </div>

          {/* Music Selection */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Choose your focus soundtrack</h4>
            {sessionData.music === 'custom' && sessionData.customMusicURL ? (
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <FaYoutube className="text-xl sm:text-2xl text-red-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-semibold text-white">Custom YouTube Music</div>
                      <div className="text-xs text-gray-400 truncate">{sessionData.customMusicURL}</div>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveCustomMusic}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCustomMusicModal(true)}
                className="w-full p-3 sm:p-4 rounded-xl sm:rounded-2xl font-semibold transition-all duration-300 bg-slate-700/30 text-gray-300 border border-slate-600/30 hover:bg-slate-600/30 flex items-center justify-center gap-2 mb-3 sm:mb-4 text-sm sm:text-base"
              >
                <FaYoutube className="text-lg sm:text-xl text-red-400" />
                <span>Add Custom YouTube Music</span>
              </button>
            )}
          </div>

          <button
            onClick={onCreateRoom}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/25 hover:scale-105"
          >
            Create Focus Room
          </button>

          {/* Custom Music Modal */}
          <CustomMusicModal
            isOpen={showCustomMusicModal}
            onClose={() => setShowCustomMusicModal(false)}
            onSave={handleCustomMusicSave}
            currentUrl={sessionData.customMusicURL || ''}
          />
        </div>
      )}

      {/* Join Room Section */}
      {teamTab === 'join' && (
        <div className="space-y-4 sm:space-y-6">
          <div>
            <input
              type="text"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value)}
              placeholder="Enter Room Code or Invite Link"
              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-slate-700/50 border border-orange-500/30 rounded-xl sm:rounded-2xl text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all text-sm sm:text-base"
            />
          </div>

          {/* Recent Rooms */}
          <div className="bg-slate-700/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-600/30">
            <h4 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Recent Rooms</h4>
            <div className="space-y-2 sm:space-y-3">
              {[
                { name: 'Design Team Focus', code: 'ABC123', members: 3 },
                { name: 'Marketing Sprint', code: 'XYZ789', members: 5 },
                { name: 'Product Review', code: 'DEF456', members: 2 }
              ].map((room, index) => (
                <button
                  key={index}
                  onClick={() => setRoomCode(room.code)}
                  className="w-full p-2.5 sm:p-3 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors text-left"
                >
                  <div className="text-sm sm:text-base text-white font-medium">{room.name}</div>
                  <div className="text-xs sm:text-sm text-gray-400">{room.code} • {room.members} members</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-slate-700/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-600/30">
            <h4 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Room Members</h4>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {['JD', 'SM', 'AL'].map((initial, index) => (
                <div key={index} className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                  {initial}
                </div>
              ))}
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-600 rounded-full flex items-center justify-center text-gray-400 font-bold text-xs sm:text-sm">
                +3
              </div>
            </div>
          </div>

          <button
            onClick={onJoinRoom}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/25 hover:scale-105"
          >
            Join Focus Room
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamModeContent;

